import {
  Handle,
  Position as HandlePosition,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import type { PositionListMode } from './PositionListPanel.js';
import { layoutTree } from './treeLayout.js';
import { PERSON_CARD, buildPeopleGraph, personEdgeTypes, personNodeTypes } from './OrgPeople.js';
import '@xyflow/react/dist/style.css';
import {
  CameraOutlined,
  LoadingOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { exportPng } from '../lib/exportImage.js';
import { ToolFan } from './ToolFan.js';
import type { ExecutiveTree, OrgNode } from '@shared/types';

/**
 * ผังตำแหน่งในหน่วยงาน — ลากได้ ซูมได้ กางดูทีละกิ่งได้
 *
 * โครงของผัง
 *   กระทรวงการคลัง → สำนักงาน → หน่วยงานในสังกัด (ลึกได้ถึง 4 ชั้น) → ตำแหน่งรายอัตรา
 *
 * "กระทรวงการคลัง" เป็นโหนดที่เราสร้างขึ้นเอง ไม่มีในข้อมูล
 * เพราะไฟล์ dump มีแต่ระดับสำนักงานลงมา แต่ผู้อ่านต้องการเห็นจุดตั้งต้นเดียว
 * ตัวเลขบนโหนดนี้จึงเป็นผลรวมของทุกสำนักงานที่อยู่ในไฟล์ ไม่ใช่ทั้งกระทรวงจริง ๆ
 *
 * ⚠ ทำไมต้องกาง/ยุบ ไม่แสดงทั้งหมดทีเดียว
 * มี 67 หน่วยงานกับ 596 ตำแหน่ง ถ้าวางออกมาพร้อมกันหมดจะเป็นกำแพงกล่อง
 * ที่หาอะไรไม่เจอ และเบราว์เซอร์ต้องวาดโหนดเป็นร้อย ๆ ตัวโดยไม่มีใครได้ดู
 *
 * ────────────────────────────────────────────────────────────────
 *  สี่โหมด = สี่คำถาม กับข้อมูลชุดเดียวกัน
 * ────────────────────────────────────────────────────────────────
 *   staffing    วันนี้อัตรากำลังเต็มแค่ไหน        occupied / total
 *   retirement  ห้าปีข้างหน้าใครจะออก             retiringIn5Years + ปีที่หนักสุด
 *   forecast    ถ้าไม่บรรจุเพิ่มเลย จะเหลือเท่าไร  (occupied − retiring) / total
 *   succession  ตำแหน่งสำคัญที่ต้องเตรียมคนไว้แทน  นับจากตำแหน่งจริงในหน่วยนั้น
 *
 * ⚠ ทั้งสี่โหมดคำนวณจากวันเกษียณจริงในข้อมูล ไม่มีการประมาณค่าเชิงสถิติ
 * ไม่มีโมเดล ไม่มีการเดา — ตัวเลขที่เห็นจึงกระทบยอดกลับไปหาตัวตั้งได้ทุกตัว
 * ถ้าวันหนึ่งจะเพิ่มการพยากรณ์จริง ต้องแยกโหมดใหม่และเขียนกำกับให้ชัดว่าเป็นค่าประมาณ
 * ห้ามเอาไปปนกับสี่โหมดนี้ เพราะผู้บริหารใช้ตัวเลขพวกนี้ไปอ้างในเอกสารต่อ
 */
export type OrgFlowMode = 'staffing' | 'retirement' | 'forecast' | 'succession';

/** ทิศทางการวางผัง — บนลงล่างเหมาะกับการนำเสนอ ซ้ายไปขวาเหมาะกับผังที่ลึกหลายชั้น */
export type OrgFlowDir = 'TB' | 'LR';

/**
 * ตัวเลขที่กล่อง KPI ด้านบนกำลังชี้อยู่ — ใช้เลือกว่าบรรทัดล่างของการ์ดจะบอกอะไร
 *
 * ⚠ ชุดค่าต้องตรงกับ StatFilter ใน OrgCanvas.tsx เป๊ะ ๆ
 *   ไม่ import มาตรง ๆ เพราะจะกลายเป็นวงกลม (OrgCanvas import OrgFlow อยู่แล้ว)
 *   ถ้าเพิ่มกล่องที่ห้าเมื่อไร ต้องเพิ่มทั้งสองที่
 */
export type OrgFlowMetric = 'total' | 'occupied' | 'vacant' | 'retiring';

/*
 * เดิมมี MINISTRY_ID = '__ministry__' อยู่ตรงนี้ — โหนดกระทรวงปลอมที่หน้าจอสร้างเอง
 *
 * มันมีอยู่เพราะสมัยนั้นเซิร์ฟเวอร์ส่งผังมาเป็นกรมหลายต้นลอย ๆ ไม่มีอะไรครอบ
 * หน้าจอจึงต้องปั้นรากขึ้นมาเองแล้วบวกยอดของทุกต้นเข้าด้วยกัน
 *
 * ตอนนี้ /api/org-tree ส่งรากจริงมาให้แล้ว (กระทรวง → กลุ่มภารกิจ → กรม)
 * ถ้ายังปั้นรากปลอมไว้ ผังจะมีการ์ดกระทรวงสองใบซ้อนกัน
 * และใบปลอมจะบวกยอดผิดด้วย เพราะมันรวมเฉพาะยอดของลูก ไม่นับอัตราที่สังกัดรากโดยตรง
 */

/* ================================================================
   เกณฑ์สถานะกำลังพล
   ================================================================
   ใช้ชุดเดียวกันทั้งการ์ด คำอธิบายสัญลักษณ์ และแผงข้าง
   ถ้าจะแก้เกณฑ์ แก้ที่นี่ที่เดียว — อย่ากระจายตัวเลข 0.9 / 0.6 ไปเขียนซ้ำที่อื่น
   เพราะวันที่ผู้ใช้ขอเปลี่ยนเกณฑ์ จะหาไม่เจอว่าซ่อนอยู่กี่ที่ */
export const FILL_BANDS = [
  { min: 0.9, key: 'ok', label: 'ปกติ', color: 'var(--brand)', note: 'อัตรากำลังเพียงพอต่อการปฏิบัติภารกิจ' },
  { min: 0.6, key: 'watch', label: 'เฝ้าระวัง', color: 'var(--gold)', note: 'อัตรากำลังลดลง อาจกระทบภารกิจบางส่วน' },
  { min: 0, key: 'crit', label: 'วิกฤต', color: 'var(--alert)', note: 'ขาดแคลนบุคลากรมาก ควรเร่งบรรจุทดแทน' },
] as const;

export function bandOf(ratio: number): (typeof FILL_BANDS)[number] {
  return FILL_BANDS.find((b) => ratio >= b.min) ?? FILL_BANDS[FILL_BANDS.length - 1]!;
}

/* ================================================================
   เกณฑ์ "ใกล้เกษียณ"
   ================================================================
   สองชั้น ใช้ร่วมกันทั้งการ์ดตำแหน่งในผัง แผงรายชื่อ และหน้าต่างรายละเอียด

     ≤ 2 ปี  ทอง   ต้องเริ่มหาคนแทนแล้ว — ตรงกับเกณฑ์ของโหมด "ตำแหน่งระดับสูงที่จะว่าง"
     ≤ 5 ปี  จาง   อยู่ในกรอบเดียวกับตัวเลข "จะเกษียณใน 5 ปี" ที่แถบบนและแผงหน่วยงานบอก

   ⚠ ห้ามตั้งเลขพวกนี้ซ้ำที่อื่น
   ก่อนหน้านี้เลข 2 ถูกเขียนตรง ๆ อยู่สามที่ (การ์ดตำแหน่ง · countSuccession · หน้าต่างรายละเอียด)
   วันที่ผู้ใช้ขอเปลี่ยนเกณฑ์จะแก้เจอบ้างไม่เจอบ้าง แล้วสองหน้าจอจะบอกไม่ตรงกัน
   โดยที่ไม่มีอะไรฟ้อง เพราะทั้งคู่ยัง build ผ่าน */
export const RETIRE_SOON_YEARS = 2;
export const RETIRE_WATCH_YEARS = 5;

/**
 * ป้ายบอกว่าคนนี้ใกล้เกษียณแค่ไหน — คืน null ถ้ายังไกลหรือไม่รู้วันเกษียณ
 *
 * ใช้ retireInYears เป็นตัวตัดสิน ไม่ใช่ปีงบประมาณ เพราะคนที่เกษียณ ก.ย. ปีหน้า
 * กับคนที่เกษียณ ต.ค. ปีหน้า ต่างกันแค่เดือนเดียว แต่คนละปีงบ
 */
export function retireMark(inc: { retireInYears: number | null; retireDate: string | null } | null):
  | { tone: 'soon' | 'watch'; label: string }
  | null {
  const y = inc?.retireInYears;
  if (y === null || y === undefined || y > RETIRE_WATCH_YEARS) return null;
  const when = inc?.retireDate ? thaiShort(inc.retireDate) : null;
  return {
    tone: y <= RETIRE_SOON_YEARS ? 'soon' : 'watch',
    label: when ? `เกษียณ ${when}` : `เกษียณในอีก ${y.toFixed(1)} ปี`,
  };
}

/** ปีงบประมาณปัจจุบัน — ปีงบเริ่ม 1 ต.ค. จึงบวกหนึ่งตั้งแต่เดือนตุลาคม */
const CURRENT_FY =
  new Date().getUTCMonth() >= 9 ? new Date().getUTCFullYear() + 544 : new Date().getUTCFullYear() + 543;

/** ระดับตำแหน่งที่ถือว่า "สำคัญ" สำหรับโหมดเตรียมผู้สืบทอด
    ยึดตามคำที่ปรากฏจริงในข้อมูล DPIS ไม่ใช่รหัสระดับ เพราะรหัสไม่ได้มาครบทุกแถว */
const SENIOR_WORDS = ['บริหาร', 'อำนวยการ', 'ทรงคุณวุฒิ', 'เชี่ยวชาญ'];

function isSeniorLevel(level: string): boolean {
  return SENIOR_WORDS.some((w) => level.includes(w));
}

/**
 * นับตำแหน่งสำคัญที่ต้องเตรียมคนไว้แทน ในหน่วยนี้และหน่วยย่อยทั้งหมด
 *
 * นับสองอย่างรวมกันโดยตั้งใจ เพราะทั้งคู่คือ "เก้าอี้ที่ไม่มีคนนั่งภายในสองปี"
 *   - ว่างอยู่แล้ววันนี้
 *   - มีคนอยู่ แต่ผู้ครองจะเกษียณภายใน 2 ปี
 * แยกตัวเลขสองก้อนนี้ไว้ให้แผงข้างแจกแจงต่อได้ ไม่ยุบเป็นตัวเลขเดียว
 */
function countSuccession(unit: OrgNode): { open: number; soon: number } {
  let open = 0;
  let soon = 0;
  const walk = (n: OrgNode) => {
    for (const p of n.positions ?? []) {
      if (!isSeniorLevel(p.level)) continue;
      if (p.vacant) open += 1;
      else {
        const y = p.incumbent?.retireInYears;
        if (y !== null && y !== undefined && y <= RETIRE_SOON_YEARS) soon += 1;
      }
    }
    for (const c of n.children ?? []) walk(c);
  };
  walk(unit);
  return { open, soon };
}

/* ================================================================
   การ์ดหน่วยงาน
   ================================================================
   โครงของการ์ดคงที่ทุกโหมด — ป้ายชั้น · ชื่อ · บรรทัดตัวเลข · แถบสัดส่วน
   เปลี่ยนแค่ "ตัวเลขไหนขึ้นแถบ" ตามโหมด

   ที่ต้องคงที่เพราะผู้ใช้สลับโหมดไปมาเพื่อเทียบ ถ้าโครงการ์ดขยับตามโหมด
   สายตาต้องหาตำแหน่งตัวเลขใหม่ทุกครั้ง แล้วการเทียบจะช้ากว่าการเปิดสองจอ */

type UnitNodeData = {
  label: string;
  /** ป้ายบอกชั้น เช่น กระทรวง / กลุ่มภารกิจ / กรม — มาจากความลึกในผัง */
  kind: string;
  total: number;
  occupied: number;
  vacant: number;
  retiring: number;
  /** ผู้ที่จะเกษียณในปีงบประมาณปัจจุบัน — ใช้ตอนผู้ใช้กดกล่อง "เกษียณปีงบ …" */
  retiringNow: number;
  metric: OrgFlowMetric;
  peakYear: number | null;
  peakCount: number;
  succOpen: number;
  succSoon: number;
  mode: OrgFlowMode;
  dir: OrgFlowDir;
  childUnits: number;
  open: boolean;
  isMinistry: boolean;
  /**
   * เป็นรากของผังที่กำลังวาดอยู่ไหม — ไม่ใช่เรื่องเดียวกับ isMinistry
   *
   * ตอนเจาะดูกรมเดียว รากคือกรมนั้น ซึ่งไม่ใช่กระทรวง
   * ⚠ ใช้ตัดสินว่ากดชื่อแล้วควร "เจาะเข้าไป" หรือ "ถอยออกมา" — กดชื่อของหน่วย
   *   ที่เจาะอยู่แล้วต้องไม่เป็นปุ่มที่กดแล้วไม่เกิดอะไร ซึ่งแย่กว่าไม่มีปุ่ม
   */
  isDrawnRoot: boolean;
  selected: boolean;
  onToggle: (id: string) => void;
  /**
   * เปิดบัญชีอัตราของหน่วยนี้ — เรียกจากปุ่มตัวเลขบนการ์ดเท่านั้น
   *
   * 'all' = ทุกอัตราทั้งที่มีคนและที่ว่าง · 'occupied' = เฉพาะที่มีผู้ครอง
   * 'vacant' = เฉพาะที่ยังไม่มีผู้ครอง
   * โหมดมาจากปุ่มที่ผู้ใช้กด ไม่ใช่จากสถานะอื่นของหน้าจอ ผลลัพธ์จึงตรงกับเลขที่เพิ่งกดเสมอ
   */
  onOpenList: (id: string, mode: PositionListMode) => void;
  /** เปิดบัญชีตำแหน่งที่ปลัดกระทรวงมีอำนาจแต่งตั้ง — มีเฉพาะการ์ดกระทรวง */
  onExecutives: (() => void) | null;
  /**
   * เจาะดูเฉพาะสายนี้ — กดที่ "ชื่อหน่วยงาน" บนการ์ด
   *
   * ⚠ ผูกไว้ที่ชื่อ ไม่ใช่ทั้งใบ — เป้ากดใหญ่เท่าการ์ดเคยถูกเอาออกไปแล้วรอบหนึ่ง
   *   เพราะผู้ใช้กดโดนโดยไม่ตั้งใจ (ดูหมายเหตุเหนือ .ncard-body) การเอากลับมา
   *   ในรูปเดิมคือการพาบั๊กเดิมกลับมาด้วย ชื่อหน่วยเป็นเป้าที่เล็กพอ ตรงความหมาย
   *   และไม่ทับกับปุ่มตัวเลขสามช่องข้างล่าง
   *
   * ส่ง null เป็น id เมื่อกดการ์ดกระทรวง = กลับไปดูภาพรวมทั้งกระทรวง
   */
  onScope: ((id: string | null) => void) | null;
};

type UnitNode = Node<UnitNodeData, 'unit'>;

/** ตัวเลขไทยแบบมีคอมมา ใช้ทุกที่ในการ์ดเพื่อให้หลักตรงกัน */
const num = (n: number) => n.toLocaleString('th-TH');

/**
 * ตัวเลขที่จะขึ้นหัวการ์ดและแถบสัดส่วน แยกตามโหมด
 *
 * ⚠ headline กับ ratio เป็นคนละเรื่องกัน อย่ารวบเป็นตัวเดียว
 *   headline คือ "ข้อความที่พิมพ์ลงการ์ด" ส่วน ratio คือ "แถบเต็มแค่ไหน"
 *   โหมดอัตรากำลังพิมพ์แค่กรอบทั้งหมด แต่แถบยังเต็มตามสัดส่วนผู้ครองตำแหน่ง
 *   ถ้าผูกสองอย่างเข้าด้วยกัน แถบจะเต็มร้อยเสมอและสีของแถบจะไม่มีความหมายอีก
 *
 * ตัดสินใจที่นี่ที่เดียว ชั้นวาดการ์ดจึงไม่ต้องมี if ของโหมดเลย
 * caption คือคำอธิบายซ้ายมือ ต้องบอกให้ได้ว่าตัวเลขที่เห็นคืออะไร
 * โดยไม่ต้องเลื่อนสายตาไปอ่านปุ่มโหมดด้านบน
 */
/**
 * บรรทัดล่างของการ์ด — เปลี่ยนตามกล่อง KPI ที่ผู้ใช้กดอยู่
 *
 * กดกล่องแล้วผังถูกตัดให้เหลือเฉพาะหน่วยที่เข้าเงื่อนไข แต่ถ้าการ์ดยังบอก
 * "ว่าง N อัตรา" เหมือนเดิม ผู้ใช้จะไม่เห็นตัวเลขที่ตัวเองกำลังตามหาเลยสักที่
 * ต้องเลื่อนสายตากลับไปอ่านกล่องด้านบนแล้วเดาเอาว่าหน่วยไหนมีเท่าไร
 *
 * ⚠ หัวการ์ดไม่เปลี่ยนตามตัวกรอง คงเป็นกรอบอัตรากำลังเสมอ
 *   เพราะมันคือตัวตั้งที่ใช้เทียบ ถ้าเปลี่ยนตามด้วยจะไม่เหลืออะไรให้เทียบ
 *   และผู้ใช้จะอ่านขนาดของหน่วยผิดไปทั้งผัง
 *
 * ⚠ ศูนย์ต้องไม่ขึ้นบรรทัดนี้ ตอนไม่ได้กรอง
 *   หน่วยที่ไม่มีอัตราว่างไม่ต้องประกาศว่า "ว่าง 0" — แต่ตอนกรองอยู่ต้องขึ้นเสมอ
 *   เพราะการ์ดที่ไม่มีบรรทัดล่างจะดูเหมือนไม่เกี่ยวกับตัวกรองที่กดไว้
 */
function metricNote(d: UnitNodeData): string | null {
  if (d.metric === 'retiring') return `เกษียณปีนี้ ${num(d.retiringNow)} อัตรา`;
  if (d.metric === 'occupied') return `มีผู้ครอง ${num(d.occupied)} อัตรา`;
  if (d.metric === 'vacant') return `ว่าง ${num(d.vacant)} อัตรา`;
  return d.vacant > 0 ? `ว่าง ${num(d.vacant)} อัตรา` : null;
}

function metricOf(d: UnitNodeData): {
  caption: string;
  /** ข้อความตัวเลขที่พิมพ์ลงการ์ดตรง ๆ */
  headline: string;
  /** แสดง % ต่อท้ายหรือไม่ */
  showPct: boolean;
  ratio: number;
  tone: string;
  extra: string | null;
  /**
   * ตัวเลขสามค่าของโหมดปกติ — กรอบอัตรากำลัง กับสองส่วนที่ประกอบกันเป็นยอดนั้น
   *
   * ⚠ แยกเป็นก้อนเดียว ไม่ใช่สามบรรทัดเท่ากัน เพราะสามเลขนี้ไม่ได้เท่าเทียมกัน
   *   จำนวนคน + ตำแหน่งที่ว่าง = กรอบอัตรากำลัง เสมอ การ์ดต้องอ่านออกว่าอะไรรวมอะไร
   *   โหมดอื่นแสดงค่าเดียว จึงคืน null แล้วถอยไปใช้ caption/headline ตามเดิม
   */
  stats: {
    total: string;
    occupied: string;
    /**
     * ผู้ครองที่จะเกษียณภายในห้าปี — เป็น "ส่วนหนึ่งของจำนวนคน" ไม่ใช่ส่วนที่สาม
     *
     * ⚠ วางไว้ระหว่างจำนวนคนกับตำแหน่งที่ว่างตามที่ผู้ใช้ขอ ซึ่งชนกับกติกาเดิม
     *   ที่ว่า "จำนวนคน + ตำแหน่งที่ว่าง = กรอบอัตรากำลัง" — เลขกลางไม่ได้บวกเข้าไปด้วย
     *   จึงต้องทำให้ตาแยกออกว่ามันเป็นเซตย่อย ไม่ใช่ยอดที่บวกกัน
     *   (ช่องกลางเป็นตัวเลขเฉย ๆ ไม่ใช่ปุ่ม · สีทองแบบเดียวกับเรื่องเกษียณทั้งระบบ
     *    · ป้ายขึ้นต้นด้วย "ใน" · และ aria-label พูดออกมาตรง ๆ ว่าเป็นส่วนหนึ่ง)
     */
    retiring: string;
    retiringRaw: number;
    vacant: string;
    hasGap: boolean;
  } | null;
} {
  if (d.mode === 'retirement') {
    const ratio = d.occupied > 0 ? 1 - d.retiring / d.occupied : 1;
    return {
      caption: 'เกษียณภายใน 5 ปี',
      headline: `${num(d.retiring)} / ${num(d.occupied)}`,
      showPct: true,
      ratio,
      tone: d.retiring > 0 ? 'var(--gold)' : 'var(--brand)',
      extra: d.peakYear !== null ? `หนักสุดปี ${d.peakYear} (${num(d.peakCount)} คน)` : null,
      stats: null,
    };
  }
  if (d.mode === 'forecast') {
    const left = Math.max(d.occupied - d.retiring, 0);
    const ratio = d.total > 0 ? left / d.total : 0;
    return {
      caption: 'คงเหลือเมื่อครบ 5 ปี',
      headline: `${num(left)} / ${num(d.total)}`,
      showPct: true,
      ratio,
      tone: bandOf(ratio).color,
      extra:
        d.retiring > 0
          ? `ลดลง ${num(d.retiring)} คนจากการเกษียณอายุราชการ`
          : 'ไม่มีผู้เกษียณอายุราชการในช่วงนี้',
      stats: null,
    };
  }
  if (d.mode === 'succession') {
    const need = d.succOpen + d.succSoon;
    return {
      caption: 'ตำแหน่งระดับสูงที่จะว่าง',
      headline: num(need),
      showPct: false,
      ratio: need > 0 ? 0 : 1,
      tone: need > 0 ? 'var(--alert)' : 'var(--brand)',
      extra:
        need > 0
          ? `ว่างแล้ว ${num(d.succOpen)} · เกษียณภายใน 2 ปี ${num(d.succSoon)}`
          : 'ไม่มีตำแหน่งระดับสูงที่จะว่างในช่วงนี้',
      stats: null,
    };
  }
  const ratio = d.total > 0 ? d.occupied / d.total : 0;
  return {
    /*
     * หัวการ์ดแสดง "กรอบอัตรากำลัง" ตัวเดียว ไม่ใช่ผู้ครอง/กรอบ กับ %
     *
     * ⚠ caption ต้องเปลี่ยนตามด้วย ห้ามทิ้งไว้ว่า "ผู้ครองตำแหน่ง"
     *   ตัวเลขที่อยู่ข้างมันคือกรอบทั้งหมด ไม่ใช่จำนวนคน ถ้าป้ายไม่ตรงกับเลข
     *   ผู้อ่านจะเอาเลขกรอบไปใช้เป็นจำนวนคนโดยไม่รู้ตัว
     *
     * จำนวนผู้ครองตำแหน่งไม่ได้หายไป ยังอ่านได้จากแถบสัดส่วนใต้บรรทัดนี้
     * จากบรรทัด "ว่าง N อัตรา" และจากแผงรายละเอียดตอนกดการ์ด
     */
    caption: 'กรอบอัตรากำลัง',
    headline: num(d.total),
    showPct: false,
    ratio,
    tone: bandOf(ratio).color,
    /* ยอด "ว่าง" อยู่ใน stats แล้ว ถ้าปล่อย extra ไว้จะมีเลขเดียวกันสองที่ในใบเดียว */
    extra: null,
    stats: {
      total: num(d.total),
      occupied: num(d.occupied),
      retiring: num(d.retiring),
      retiringRaw: d.retiring,
      vacant: num(d.vacant),
      hasGap: d.vacant > 0,
    },
  };
}

function UnitCard({ id, data }: NodeProps<UnitNode>) {
  const m = metricOf(data);
  const pct = Math.round(m.ratio * 100);
  const branches = data.childUnits;

  return (
    <div
      className={`ncard${data.isMinistry ? ' is-root' : ''}${data.open ? ' is-open' : ''}${
        data.selected ? ' is-sel' : ''
      }`}
      style={{ ['--tone' as string]: m.tone }}
    >
      <Handle
        type="target"
        position={data.dir === 'TB' ? HandlePosition.Top : HandlePosition.Left}
        style={{ opacity: 0 }}
      />

      {/* ทั้งใบกดได้ = เปิดแผงรายละเอียด ใช้ปุ่มจริงไม่ใช่ div onClick
          เพื่อให้ไล่ด้วยแป้น Tab และกด Enter ได้เหมือนกัน */}
      {/* ⚠ เดิมทั้งใบเป็น <button> เดียว กดตรงไหนก็เปิดแผงรายละเอียด
          เอาออกตามที่ผู้ใช้ขอ เพราะเป้ากดใหญ่เท่าการ์ดทำให้กดโดนโดยไม่ตั้งใจ
          ตอนนี้กดได้เฉพาะตัวเลขสองตัวที่เป็นปุ่มจริง ๆ ข้างล่าง */}
      <div className="ncard-body">
        <span className="ncard-kind">{data.kind}</span>
        {data.onScope ? (
          <button
            type="button"
            className="ncard-name is-hit"
            onClick={() => data.onScope?.(data.isDrawnRoot ? null : id)}
            title={
              data.isDrawnRoot
                ? 'กลับไปดูภาพรวมทั้งกระทรวง'
                : `ดูเฉพาะสาย ${data.label}`
            }
          >
            {data.label}
          </button>
        ) : (
          <span className="ncard-name">{data.label}</span>
        )}

        {m.stats ? (
          <span className="ncard-stats">
            <button
              type="button"
              className="lead ncard-num"
              onClick={() => data.onOpenList(id, 'all')}
              aria-label={`ดูทุกอัตราของ ${data.label} ${m.stats.total} อัตรา`}
            >
              <span className="n tnum">{m.stats.total}</span>
              <span className="k">กรอบอัตรากำลัง</span>
            </button>
            <span className="split">
              <button
                type="button"
                className="cell ncard-num"
                onClick={() => data.onOpenList(id, 'occupied')}
                aria-label={`ดูอัตราที่มีผู้ครองของ ${data.label} ${m.stats.occupied} อัตรา`}
              >
                <span className="n tnum">{m.stats.occupied}</span>
                <span className="k">จำนวนคน</span>
              </button>
              {/* ⚠ ป้ายและ aria-label พูดออกมาเต็ม ๆ ว่าเป็นส่วนหนึ่งของจำนวนคน
                  ไม่ใช่ยอดที่สามที่เอาไปบวกกับอีกสองช่อง — สามตัวเลขเรียงกันอ่านว่าบวกกันได้เสมอ
                  ⚠ ป้าย "เกษียณอีก 5 ปี" ยาวกว่าช่องที่ตัวอักษร 10px จึงลดเหลือ 9.5px
                    เฉพาะช่องนี้ (.cell.is-sub .k ใน styles.css) วัดด้วยเบราว์เซอร์จริงแล้ว
                    ⚠ ห้ามแก้เป็นป้ายยาวกว่านี้โดยไม่วัดใหม่ — การ์ดกว้าง 248px และความกว้างนั้น
                      ถูกใช้คำนวณตำแหน่งวางทั้งผัง (CARD_W ใน treeLayout) */}
              <button
                type="button"
                className="cell ncard-num is-sub"
                onClick={() => data.onOpenList(id, 'retiring')}
                title={`ผู้ครองที่จะเกษียณภายใน 5 ปี — เป็นส่วนหนึ่งของจำนวนคน ${m.stats.occupied} คน ไม่ได้บวกเพิ่ม`}
                aria-label={`ดูผู้ที่จะเกษียณภายใน 5 ปีของ ${data.label} ${m.stats.retiring} คน จากจำนวนคนทั้งหมด ${m.stats.occupied} คน`}
              >
                <span className="n tnum">{m.stats.retiring}</span>
                <span className="k">เกษียณอีก 5 ปี</span>
              </button>
              <button
                type="button"
                className={`cell ncard-num${m.stats.hasGap ? ' is-gap' : ''}`}
                onClick={() => data.onOpenList(id, 'vacant')}
                aria-label={`ดูตำแหน่งที่ว่างของ ${data.label} ${m.stats.vacant} อัตรา`}
              >
                <span className="n tnum">{m.stats.vacant}</span>
                <span className="k">ตำแหน่งที่ว่าง</span>
              </button>
            </span>
          </span>
        ) : (
          <span className="ncard-figs">
            <span className="cap">{m.caption}</span>
            <span className="val tnum">
              {m.headline}
              {m.showPct && <b className="pct">{pct}%</b>}
            </span>
          </span>
        )}

        {m.extra && <span className="ncard-note">{m.extra}</span>}
      </div>

      {/* ---------- บัญชีตำแหน่งที่ปลัดกระทรวงมีอำนาจสั่งบรรจุและแต่งตั้ง ----------

          ⚠ ปุ่มนี้เคยหายไปทั้งที่ทุกอย่างรอบตัวมันยังอยู่ครบ และไม่มีอะไรฟ้องเลย
            .ncard-exec ใน styles.css ยังอยู่ · data.onExecutives ยังถูกส่งมาถึง
            · TeamOutlined ยังถูก import — ขาดแค่ JSX ตรงนี้บรรทัดเดียว
            tsc ไม่ฟ้องเพราะไม่ได้เปิด noUnusedLocals ผลคือไอคอนหายจากหน้าจอเงียบ ๆ
            จนผู้ใช้มาทักเอง มีสคริปต์ check-org-card.ts กันไว้แล้ว ห้ามลบสคริปต์นั้น

          ⚠ ต้องอยู่ "นอก" .ncard-body และวางด้วย position: absolute
            ขึ้นเฉพาะการ์ดกระทรวง เพราะอำนาจตามมาตรา 57 เป็นของปลัดกระทรวง
            ไม่ใช่ของหัวหน้าหน่วยงานทุกชั้น ถ้าขึ้นทุกใบจะอ่านว่าทุกหน่วยมีบัญชีของตัวเอง */}
      {data.onExecutives && (
        <button
          type="button"
          className="ncard-exec"
          onClick={(e) => {
            e.stopPropagation();
            data.onExecutives?.();
          }}
          title="ตำแหน่งที่ปลัดกระทรวงมีอำนาจสั่งบรรจุและแต่งตั้ง"
          aria-label="ดูบัญชีตำแหน่งที่ปลัดกระทรวงมีอำนาจสั่งบรรจุและแต่งตั้ง"
        >
          <TeamOutlined aria-hidden="true" />
        </button>
      )}

      {/*
        ⚠ ปุ่มนี้ต้องอยู่ "นอก" .ncard-hit ไม่ใช่ข้างใน
        .ncard-hit เป็น <button> ที่ครอบทั้งใบไว้แล้ว ปุ่มซ้อนในปุ่มเป็น HTML ที่ผิด
        เบราว์เซอร์จะแก้โครงให้เอง แล้วผลลัพธ์ต่างกันไปในแต่ละตัว
        (ปัญหาเดียวกับปุ่มกาง/ยุบด้านล่าง ซึ่งวางไว้นอกด้วยเหตุผลเดียวกัน)
      */}
      {branches > 0 && (
        <button
          type="button"
          className="ncard-toggle"
          onClick={(e) => {
            e.stopPropagation();
            data.onToggle(id);
          }}
          aria-expanded={data.open}
          aria-label={`${data.open ? 'ยุบ' : 'กางดู'}หน่วยงานย่อยของ ${data.label}`}
        >
          <span className="sign" aria-hidden="true">{data.open ? '−' : '+'}</span>
          <span className="cnt">{num(data.childUnits)} หน่วยงาน</span>
        </button>
      )}

      <Handle
        type="source"
        position={data.dir === 'TB' ? HandlePosition.Bottom : HandlePosition.Right}
        style={{ opacity: 0 }}
      />
    </div>
  );
}

/* ⚠ ต้องประกาศนอกคอมโพเนนต์ ไม่ใช่สร้างใหม่ทุกรอบเรนเดอร์
   React Flow เทียบ nodeTypes ด้วย identity ถ้าเป็นอ็อบเจกต์ใหม่ทุกครั้ง
   มันจะถอดและสร้างโหนดทั้งผังใหม่ทุกเฟรม ผังจะกระตุกและ state ภายในโหนดหาย */
const nodeTypes = { unit: UnitCard, ...personNodeTypes };
/* ⚠ ต้องประกาศนอกคอมโพเนนต์เช่นกัน ด้วยเหตุผลเดียวกับ nodeTypes */
const edgeTypes = { ...personEdgeTypes };

/* ================================================================
   ขนาดและการวาง
   ================================================================
   อัลกอริทึมวางผังทำงานบน "แกนลึก" (ชั้นของผัง) กับ "แกนกระจาย" (พี่น้องกันเรียงต่อกัน)
   แล้วค่อยแปลงเป็น x/y ตอนท้ายตามทิศที่เลือก

   เขียนแบบนี้เพราะเดิมมีทิศเดียว พอเพิ่มบนลงล่างเข้ามา ถ้า copy อัลกอริทึมไปอีกชุด
   วันหนึ่งแก้บั๊กการวางจะแก้ที่เดียวแล้วอีกทิศยังพังอยู่ โดยไม่มีใครรู้จนกว่าจะมีคนสลับทิศ

   ⚠ ค่าช่องต้องมากกว่าขนาดจริงของการ์ดที่ใหญ่ที่สุดเสมอ
   ถ้าตั้งน้อยกว่าของจริง การ์ดจะทับกันจนกดปุ่มบนใบล่างไม่ได้

   ⚠ ตัวเลขชุดนี้ผูกกับความสูงการ์ดใน styles.css โดยตรง
     แก้ padding / font-size / line-height ของ .ncard-* เมื่อไร ต้องกลับมาคิดใหม่ทุกครั้ง
     ทุกค่าใน .ncard-kind .ncard-name .ncard-stats จึงกำหนด line-height ไว้ชัด
     ไม่ปล่อยตาม default ของเบราว์เซอร์ เพื่อให้ความสูงคำนวณย้อนได้

   ความสูงหลังรอบลดขนาด (คำนวณจาก CSS การ์ดกระทรวงซึ่งสูงที่สุด ชื่อยาวสองบรรทัด):
     9 (ขอบบน) + 13 (ป้ายชั้น) + 2 + 37.7 (ชื่อ 2 บรรทัด) + 7 (เว้น)
     + 21 (ยอดรวม) + 15 (เส้นคั่น+เว้น) + 29 (สองช่องล่าง) + 9 (ขอบล่าง)
     + 29 (แถวปุ่มกาง/ยุบ) ≈ 172px  — เดิม ~200px */
const CARD_W = 248;

const METRICS: Record<OrgFlowDir, { depth: number; slotUnit: number; slotPos: number; gap: number }> = {
  // ซ้าย→ขวา: ชั้นถัดไปห่างออกไปทางขวา พี่น้องเรียงลงล่าง ช่อง = ความสูงการ์ด
  LR: { depth: 330, slotUnit: 180, slotPos: 116, gap: 64 },
  /* บน→ล่าง: ชั้นถัดไปห่างลงล่าง พี่น้องเรียงไปทางขวา ช่อง = ความกว้างการ์ด + ช่องไฟ
     depth ลดจาก 250 เหลือ 212 พร้อมกับรอบลดความสูงการ์ด — ค่าเดิมเผื่อไว้สำหรับการ์ดสูง ~200px
     ถ้าคงไว้ เส้นเชื่อมระหว่างชั้นจะยาวขึ้นเปล่า ๆ แทนที่ผังจะสั้นลงตามการ์ด */
  TB: { depth: 212, slotUnit: CARD_W + 28, slotPos: CARD_W + 28, gap: 64 },
};


/* ---------------- ชั้นวางผัง ---------------- */

/**
 * ป้ายชั้นบนหัวการ์ด
 *
 * สามชั้นบนมาจาก node.kind ที่เซิร์ฟเวอร์ส่งมา ไม่ได้เดาจากความลึก
 *
 * ⚠ เดิมเลือกป้ายจากความลึกล้วน ๆ ด้วยรายการ
 *      ['กระทรวง', 'ส่วนราชการ', 'หน่วยงานในสังกัด', 'ส่วน/ฝ่าย', 'งาน']
 * ซึ่งเขียนไว้ตอนที่คิดว่าผังเริ่มที่กระทรวง แต่ผังจริงตอนนั้นเริ่มที่กรม
 * ผลคือการ์ดของกรมสรรพากรขึ้นป้ายว่า "กระทรวง" มาตลอดโดยไม่มีใครสังเกต
 * นั่นคือเหตุผลที่ชั้นต้องมาจากเซิร์ฟเวอร์ ไม่ใช่จากการนับระยะทางที่หน้าจอ
 *
 * ชั้นที่ลึกกว่ากรมยังใช้ความลึกอยู่ เพราะแต่ละกรมซอยหน่วยลึกไม่เท่ากัน
 * ข้อมูลจึงไม่มีทางบอกได้ว่าชั้นนั้นชื่อ "ส่วน" หรือ "ฝ่าย" — เป็นการเดาที่ยอมรับได้
 * เพราะเดาผิดแล้วได้ป้ายที่ใกล้เคียง ไม่ใช่ป้ายที่ผิดคนละชั้นแบบเดิม
 */
const KIND_LABEL: Record<string, string> = {
  ministry: 'กระทรวง',
  missionGroup: 'กลุ่มภารกิจ',
  department: 'ส่วนราชการ',
};

/** ป้ายของชั้นที่ลึกกว่ากรม นับจาก depth 3 เป็นต้นไป */
const UNIT_DEPTH_KIND = ['หน่วยงานในสังกัด', 'ส่วน/ฝ่าย', 'งาน'];

function kindOf(unit: OrgNode, depth: number): string {
  const byKind = KIND_LABEL[unit.kind];
  if (byKind) return byKind;
  const i = Math.max(depth - 3, 0);
  return UNIT_DEPTH_KIND[Math.min(i, UNIT_DEPTH_KIND.length - 1)] ?? 'หน่วยงาน';
}

/**
 * กิ่งที่กางไว้ตั้งแต่แรก — ราก และถ้ารากคือกระทรวง ให้กางกลุ่มภารกิจด้วย
 *
 * ตัดสินจาก kind ไม่ใช่จากจำนวนชั้น เพราะ OrgCanvas ส่งกรมมาเป็นรากได้เหมือนกัน
 * ตอนผู้ใช้เจาะดูกรมเดียว แล้วกรมไม่ควรถูกกางสองชั้นเหมือนกระทรวง
 */
/**
 * ผังลึกได้ไม่เกินกี่ชั้น นับรวมรากเป็นชั้นที่ 1
 *
 *   1 กระทรวง · 2 กลุ่มภารกิจ · 3 ส่วนราชการ · 4 หน่วยงานในสังกัด · 5 ส่วน/ฝ่าย
 *
 * ตรงกับป้ายที่ KIND_LABEL กับ UNIT_DEPTH_KIND มีให้พอดี ลึกกว่านี้ป้ายจะซ้ำคำว่า "งาน"
 * ไปเรื่อย ๆ โดยไม่ได้บอกอะไรเพิ่ม และแต่ละกรมซอยหน่วยลึกไม่เท่ากันอยู่แล้ว
 * ผังจึงจะเอียงข้างไปทางกรมที่ซอยละเอียดที่สุดโดยที่นั่นไม่ได้มีคนมากกว่า
 *
 * ⚠ นี่คือเพดานของ "การแสดงผล" ไม่ใช่ของข้อมูล
 *   ยอดในกล่องชั้นที่ 5 ยังรวมหน่วยย่อยที่ลึกกว่านั้นครบ (เซิร์ฟเวอร์รวมมาให้แล้ว)
 *   คนที่อยากเห็นข้างในต้องกดที่การ์ดเพื่อเปิดแผงรายละเอียด ไม่ใช่กางผังต่อ
 */
const MAX_DEPTH = 5;

/**
 * เปิดไว้แค่สองชั้นตอนโหลด — กระทรวงกับกลุ่มภารกิจ
 *
 * เปิดถึงชั้นกรมด้วยจะได้กล่อง 14 ใบเรียงกันตั้งแต่วินาทีแรก ซึ่งกว้างเกินจอ
 * แล้ว fitView จะย่อจนตัวหนังสือในกล่องอ่านไม่ออก คนที่เพิ่งเปิดหน้าจึงเห็น
 * กำแพงกล่องก่อนที่จะเห็นว่ากระทรวงแบ่งเป็นกี่กลุ่มภารกิจ ซึ่งเป็นคำถามแรกของผัง
 */
function defaultOpen(tree: OrgNode[]): Set<string> {
  const root = tree[0];
  if (!root) return new Set();
  return new Set<string>([root.id]);
}

/**
 * รายการ id ของหน่วยที่ยังต้องอยู่ในผัง เมื่อแผงข้างเปิดอยู่
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไม
 * ────────────────────────────────────────────────────────────────
 *
 * พอกางลงไปถึงชั้นส่วน/ฝ่าย แถวล่างสุดมีการ์ดสิบกว่าใบเรียงกันเลยขอบจอทั้งสองข้าง
 * ผู้ใช้ที่กดใบหนึ่งเพื่ออ่านรายละเอียดทางขวา ต้องคอยหาว่าใบที่ตัวเองกดอยู่ตรงไหน
 * ทั้งที่ตอนนั้นเขาสนใจสายเดียว
 *
 * ระหว่างที่แผงเปิดอยู่ ผังจึงเหลือเฉพาะ
 *   ๑ บรรพบุรุษตั้งแต่รากลงมาถึงหน่วยที่เลือก (พี่น้องของแต่ละชั้นถูกตัดทิ้ง)
 *   ๒ ลูกหลานของหน่วยที่เลือก ตามสถานะกาง/ยุบเดิมของมัน
 *
 * ⚠ ข้อ ๒ จำเป็น ไม่ใช่ของแถม
 *   ถ้าตัดให้เหลือแค่สายจริง ๆ ปุ่ม "+ N หน่วยงาน" บนการ์ดที่เลือกจะกดแล้วไม่มีอะไรเกิดขึ้น
 *   เพราะลูกถูกกรองทิ้งไปหมด — ปุ่มที่กดแล้วเงียบคือปุ่มที่อ่านว่าระบบพัง
 *
 * ⚠ คืนเป็น "รายชื่อ id" ไม่ใช่ tree ที่ตัดกิ่งแล้ว
 *   เคยทำเป็นตัดกิ่งจริง แล้วป้าย "N หน่วยงาน" บนการ์ดบรรพบุรุษเปลี่ยนเป็น 1 ทุกใบ
 *   เพราะมันนับจากลูกที่เหลืออยู่ ผู้ใช้จะอ่านว่ากรมนี้มีหน่วยเดียว ซึ่งไม่จริง
 *   ตัวเลขบนการ์ดต้องมาจากข้อมูลจริงเสมอ การกรองเป็นเรื่องของการวางผังเท่านั้น
 *
 * ⚠ ไม่ได้ยุ่งกับ state ของการกาง/ยุบเลย เป็นการกรองตอนวาดล้วน ๆ
 *   ปิดแผงแล้วผังกลับมาเหมือนเดิมทุกกิ่ง โดยไม่ต้องจำอะไรไว้คืนทีหลัง
 *
 * คืน null เมื่อไม่ได้เลือกอะไร หรือหาหน่วยที่เลือกไม่เจอในผัง — แปลว่า "ไม่ต้องกรอง"
 * (หน่วยที่เลือกอาจอยู่นอกผังได้ เช่น ลึกกว่า MAX_DEPTH หรือถูกตัวกรอง KPI กรองออก)
 */
/**
 * รหัสของหน่วยเหนือขึ้นไปทุกชั้นจนถึงราก — ไม่รวมตัวมันเอง
 *
 * ⚠ ใช้กางกิ่งให้เห็นหน่วยที่เลือก · `lineageIds` ข้างล่างบอกว่า "วาดอะไรบ้าง"
 *   ส่วนตัวนี้บอกว่า "ต้องกางอะไรบ้าง" — คนละเรื่องกัน ถ้ากรองอย่างเดียวโดยไม่กาง
 *   หน่วยที่เลือกจะไม่ถูกวาดเลยเมื่อกิ่งเหนือมันยังยุบอยู่ แล้วผังจะขึ้นมาว่างเปล่า
 *
 * ⚠ ไม่รวมตัวหน่วยที่เลือกโดยตั้งใจ — กางตัวมันเองเท่ากับกางลูกทั้งหมดของมันออกมาด้วย
 *   ทั้งที่ผู้ใช้เพิ่งขอดูเส้นทางลงมาถึงมัน ไม่ได้ขอดูข้างในมัน
 */
function ancestorPath(tree: OrgNode[], target: string | null): string[] {
  if (!target) return [];
  const path: string[] = [];
  const walk = (node: OrgNode, trail: string[]): boolean => {
    if (node.id === target) {
      path.push(...trail);
      return true;
    }
    for (const kid of node.children ?? []) if (walk(kid, [...trail, node.id])) return true;
    return false;
  };
  for (const root of tree) if (walk(root, [])) break;
  return path;
}

function lineageIds(tree: OrgNode[], selected: string | null): Set<string> | null {
  if (!selected) return null;

  const keep = new Set<string>();

  const addSubtree = (node: OrgNode) => {
    keep.add(node.id);
    for (const kid of node.children ?? []) addSubtree(kid);
  };

  /** เดินหาเส้นทางจากรากถึงหน่วยที่เลือก คืน true ถ้ากิ่งนี้พาไปถึง */
  const walk = (node: OrgNode): boolean => {
    if (node.id === selected) {
      addSubtree(node);
      return true;
    }
    for (const kid of node.children ?? []) {
      if (walk(kid)) {
        keep.add(node.id);
        return true;
      }
    }
    return false;
  };

  for (const root of tree) walk(root);
  return keep.size > 0 ? keep : null;
}
/**
 * ⚠ export ไว้เพื่อให้ scripts/dump-layout.ts เรียกได้ ไม่ใช่เพื่อให้หน้าจออื่นเรียก
 *   ผังทุกผังต้องผ่าน <OrgFlow> ไม่ใช่ประกอบโหนดเองแล้วโยนใส่ ReactFlow
 */
export function buildGraph(
  tree: OrgNode[],
  mode: OrgFlowMode,
  dir: OrgFlowDir,
  open: Set<string>,
  selected: string | null,
  onToggle: (id: string) => void,
  onOpenList: (id: string, mode: PositionListMode) => void,
  /** กดชื่อหน่วยบนการ์ด — undefined = ชื่อเป็นข้อความธรรมดา กดไม่ได้ */
  onScope: ((id: string | null) => void) | undefined,
  onExecutives: (() => void) | undefined,
  metric: OrgFlowMetric,
  fiscalYear: number,
  /** null = วาดทุกกิ่ง · มีค่า = วาดเฉพาะ id ที่อยู่ในชุดนี้ (ดู lineageIds) */
  lineage: Set<string> | null,
): { nodes: UnitNode[]; edges: Edge[] } {
  const nodes: UnitNode[] = [];
  const edges: Edge[] = [];
  const M = METRICS[dir];

  const link = (from: string, to: string) => {
    edges.push({
      id: `${from}->${to}`,
      source: from,
      target: to,
      type: 'smoothstep',
      /* ⚠ ใช้ --edge ไม่ใช่ --graph2 — เส้นในผังเป็นทองตามที่เจ้าของระบบสั่ง
         ส่วน --graph2 ยังเป็นน้ำเงินจาง เพราะถูกใช้เป็นสีจุดและลูกศรในแผนภาพอื่น
         (ดูคำอธิบายที่โทเคน --edge ใน styles.css) */
      style: { stroke: 'var(--edge)', strokeWidth: 1.3 },
    });
  };

  /*
   * ไม่ต้องมี kindBase อีกแล้ว
   *
   * เดิมต้องคำนวณตัวเลื่อนชั้น เพราะตอนเจาะดูหน่วยเดียว หน่วยนั้นอยู่ชั้น 0 ของผัง
   * ที่กำลังวาด แต่ในกระทรวงจริงมันคือกรม ตอนนี้ node.depth เป็นชั้นจริงในกระทรวง
   * ที่เซิร์ฟเวอร์กำหนดมาแล้ว และ node.kind บอกชั้นตรง ๆ อยู่แล้ว
   * ป้ายจึงถูกทั้งตอนดูทั้งกระทรวงและตอนเจาะดูกรมเดียว โดยไม่ต้องเลื่อนอะไร
   */

  /**
   * ผังมีรากเดียวเสมอ ไม่ว่าจะดูทั้งกระทรวงหรือเจาะดูกรมเดียว
   *
   * ดูทั้งกระทรวง  → ราก = กระทรวงการคลัง (เซิร์ฟเวอร์ส่งมา)
   * เจาะดูกรมเดียว → ราก = กรมนั้น (OrgCanvas ส่ง [scoped] มา)
   *
   * ⚠ ต้องใช้ตัวหน่วยนั้นเป็นรากตรง ๆ ห้ามส่ง children ของมันมาแล้วสร้างรากปลอมสวมชื่อมันแทน
   * เพราะยอดของรากปลอมจะเป็นผลรวมของลูกเท่านั้น ไม่นับอัตราที่สังกัดหน่วยนั้นโดยตรง
   * ตัวเลขบนหัวผังจะน้อยกว่าความจริงโดยไม่มีอะไรบอก
   */
  const only = tree[0];
  if (!only) return { nodes, edges };

  /* ⚠ การวางตำแหน่งอยู่ที่ treeLayout.ts ที่เดียว ห้ามเขียนอัลกอริทึมซ้ำที่นี่
     ผังคนระดับ 9 ขึ้นไปใช้ตัวเดียวกัน ถ้าแยกกันวันหนึ่งจะแก้บั๊กได้ผังเดียว */
  const { placed, links } = layoutTree(only, {
    idOf: (n) => n.id,
    childrenOf: (n) => n.children ?? [],
    /* ⚠ ป้าย "N หน่วยงาน" ต้องนับจากลูกทั้งหมด ไม่ใช่จากลูกที่วาดอยู่
       ตอนกรองสายจะเหลือลูกใบเดียว ถ้าเอาตัวที่วาดไปทำป้ายด้วย
       การ์ดจะบอกว่ากรมนี้มีหน่วยเดียว ซึ่งไม่จริง */
    drawnChildrenOf: (_n, all) => (lineage ? all.filter((k) => lineage.has(k.id)) : all),
    isOpen: (n) => open.has(n.id),
    maxDepth: MAX_DEPTH,
    slot: M.slotUnit,
    depthStep: M.depth,
    /* ⚠ ผังหน่วยงานไม่จัดใบเป็นคอลัมน์ ตั้งใจให้ต่างจากผังคน
       หน่วยงานมีลูกไม่กี่หน่วยต่อชั้น กางออกข้างแล้วยังอ่านได้
       และผู้ใช้คุ้นกับรูปทรงเดิมของผังนี้มาตลอด การเปลี่ยนรูปทรง
       ของผังที่ใช้อยู่ทุกวันโดยไม่มีใครขอ คือการเปลี่ยนที่ต้องอธิบาย */
  });

  for (const p of placed) {
    nodes.push(
      unitNode(
        p.node, p.depth, p.depthPos, p.spreadPos, mode, dir, onExecutives,
        p.allChildren.length, p.expanded, metric, fiscalYear, selected, onToggle, onOpenList, onScope,
      ),
    );
  }
  for (const [from, to] of links) link(from, to);

  return { nodes, edges };
}

function unitNode(
  unit: OrgNode,
  depth: number,
  /** ตำแหน่งบนแกนลึกเป็นพิกเซล — มาจาก treeLayout ห้ามคำนวณจาก depth เอง */
  depthPos: number,
  spread: number,
  mode: OrgFlowMode,
  dir: OrgFlowDir,
  onExecutives: (() => void) | undefined,
  childUnits: number,
  isOpen: boolean,
  metric: OrgFlowMetric,
  fiscalYear: number,
  selected: string | null,
  onToggle: (id: string) => void,
  onOpenList: (id: string, mode: PositionListMode) => void,
  onScope: ((id: string | null) => void) | undefined,
): UnitNode {
  const entries = Object.entries(unit.retiringByYear ?? {})
    .map(([y, n]) => [Number(y), n] as const)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0] - b[0]);
  const peak = entries[0];
  // นับเฉพาะโหมดที่ใช้จริง — เดินต้นไม้ทั้งกิ่งทุกใบทุกโหมดคือค่าใช้จ่ายที่ไม่ได้ใช้
  const succ = mode === 'succession' ? countSuccession(unit) : { open: 0, soon: 0 };

  return {
    id: unit.id,
    type: 'unit',
    /* ⚠ ใช้ depthPos ที่ treeLayout คำนวณมา ไม่ใช่ depth * M.depth
       ผังคนมีใบที่ไล่ลงไปตามแกนลึกทั้งที่อยู่ชั้นเดียวกัน การคำนวณจาก depth
       จะได้ตำแหน่งที่ทับกันหมด (ผังหน่วยงานไม่ได้ใช้ท่านั้น แต่ต้องผ่านทางเดียวกัน) */
    position: dir === 'LR' ? { x: depthPos, y: spread } : { x: spread, y: depthPos },
    data: {
      label: unit.name,
      kind: kindOf(unit, unit.depth),
      total: unit.total,
      occupied: unit.occupied,
      vacant: unit.vacant,
      retiring: unit.retiringIn5Years,
      retiringNow: unit.retiringByYear?.[fiscalYear] ?? 0,
      metric,
      peakYear: peak ? peak[0] : null,
      peakCount: peak ? peak[1] : 0,
      succOpen: succ.open,
      succSoon: succ.soon,
      mode,
      dir,
      childUnits,
      open: isOpen,
      // สไตล์การ์ดรากมาจากชั้นจริงที่เซิร์ฟเวอร์บอก ไม่ใช่จากโหนดปลอมที่หน้าจอปั้นเอง
      isMinistry: unit.kind === 'ministry',
      isDrawnRoot: depth === 0,
      // ไอคอนบัญชีผู้บริหารขึ้นเฉพาะการ์ดกระทรวง และเฉพาะเมื่อตัวครอบส่งฟังก์ชันมาให้
      onExecutives: unit.kind === 'ministry' && onExecutives ? onExecutives : null,
      selected: selected === unit.id,
      onToggle,
      onOpenList,
      onScope: onScope ?? null,
    },
  };
}

/** posNo ซ้ำกันได้ในข้อมูลจริง จึงต้องผสมชื่อหน่วยและชื่อตำแหน่งให้ id ไม่ชนกัน */
/* ปุ่มเครื่องมือที่คลี่ออกเป็นส่วนโค้ง — ย้ายไป ToolFan.tsx เพื่อใช้ร่วมกับผังผู้บริหาร (19 ก.ย. 2569) */

/* ================================================================
   ตัวผังเอง
   ================================================================ */

export interface OrgFlowProps {
  tree: OrgNode[];
  mode?: OrgFlowMode;
  /** กล่อง KPI ที่กำลังกดอยู่ — บรรทัดล่างของการ์ดจะบอกตัวเลขของกล่องนั้น
      ⚠ ห้ามตั้งชื่อว่า focus — ชนกับ state ชื่อเดียวกันที่ใช้เลื่อนผังไปหาหน่วยที่ค้นเจอ */
  metric?: OrgFlowMetric;
  /** ปีงบประมาณปัจจุบัน มาจากเซิร์ฟเวอร์ ไม่ให้หน้าจอเดาเอง */
  fiscalYear: number;
  dir?: OrgFlowDir;
  /** กดที่การ์ดหน่วยงาน — ให้หน้าที่ครอบอยู่เปิดแผงรายละเอียดเอง */
  onOpenList?: (unitId: string, mode: PositionListMode) => void;
  /**
   * กดชื่อหน่วยงานบนการ์ด — ให้หน้าที่ครอบเปลี่ยนขอบเขตไปดูเฉพาะสายนั้น
   *
   * ⚠ null = กลับไปภาพรวมทั้งกระทรวง (ส่งมาจากการ์ดกระทรวง)
   *   ไม่ส่ง prop นี้เลย = ชื่อหน่วยเป็นข้อความธรรมดา กดไม่ได้ ซึ่งถูกต้องแล้วสำหรับ
   *   ผังที่ไม่มีที่ให้เปลี่ยนขอบเขต เช่นผังที่ฝังอยู่ในหน้าต่างอื่น
   */
  onScope?: (unitId: string | null) => void;
  /** หน่วยที่แผงข้างกำลังเปิดอยู่ ใช้ไฮไลต์การ์ดให้รู้ว่าแผงที่เห็นเป็นของใบไหน */
  selected?: string | null;
  /** ซ่อนแถบปุ่มในตัวผัง เมื่อหน้าที่ครอบมีแถบเครื่องมือของตัวเองแล้ว */
  bare?: boolean;
  /**
   * กดไอคอนบนการ์ดกระทรวง — เปิดบัญชีตำแหน่งที่ปลัดกระทรวงมีอำนาจแต่งตั้ง
   *
   * ⚠ ไอคอนจะขึ้นเฉพาะการ์ดที่ kind === 'ministry' เท่านั้น
   * ไม่ใช่ทุกการ์ด เพราะอำนาจตามมาตรา 57 ผูกกับปลัดกระทรวง ไม่ได้ผูกกับหัวหน้าหน่วยทั่วไป
   */
  onExecutives?: () => void;
  /**
   * ผังคนระดับ 9 ขึ้นไป — มีค่าเมื่อไร ผังจะสลับจากผังหน่วยงานมาเป็นผังคน
   *
   * ⚠ ใช้ผืนผ้าใบเดิม ไม่ใช่คอมโพเนนต์ใหม่ซ้อนทับ ผู้ใช้จึงได้เครื่องมือชุดเดิมครบ
   *   (เต็มจอ · สลับทิศ · บันทึกรูป · ซูม) โดยไม่ต้องทำแถบเครื่องมือชุดที่สอง
   *
   * ⚠ null กับ undefined ต่างกัน — undefined คือหน้าที่ครอบไม่รองรับผังคนเลย
   *   ส่วน null คือรองรับแต่ยังโหลดไม่เสร็จ ตัวผังจึงต้องยังวาดผังหน่วยงานอยู่
   */
  people?: ExecutiveTree | null;
  /** ผังคนแสดงถึงลำดับไหน — ไม่ส่ง = ทุกลำดับ */
  peopleMaxTier?: number;
  /** กดการ์ดคนในผังคน — ให้หน้าที่ครอบเปิดรายละเอียดอัตรานั้นเอง */
  onOpenPosition?: (posId: string) => void;
  /*
   * เดิมมี prop `bareRoot` ตรงนี้ ไว้บอกว่าให้ใช้ tree[0] เป็นรากตรง ๆ หรือให้หน้าจอ
   * ปั้นโหนด "กระทรวงการคลัง" ครอบให้ ตอนนี้เซิร์ฟเวอร์ส่งรากจริงมาเสมอ ผังจึงมีรากเดียว
   * ทั้งสองกรณี และไม่ต้องบอกอะไรอีก
   *
   * ⚠ ที่ยังต้องทำเหมือนเดิมคือ ตัวที่ครอบต้องใส่ key ให้ผังเมื่อสลับขอบเขต
   * ไม่งั้นกิ่งที่กางค้างจากขอบเขตก่อนหน้าจะติดมาด้วย แล้วผังจะเปิดมาในสภาพที่ผู้ใช้ไม่ได้สั่ง
   */
  /**
   * ปุ่มเพิ่มเติมที่จะไปอยู่ในแถบเครื่องมือมุมขวาบนของผัง
   *
   * มีไว้ให้หน้าที่ครอบเอาปุ่มของตัวเอง (เช่น ปุ่มเต็มจอ) มาต่อท้ายในแถบเดียวกันได้
   * แทนที่จะทำแถบลอยของตัวเองอีกอันซ้อนกันคนละมุม ซึ่งผู้ใช้ต้องกวาดตาหาสองที่
   */
  toolsExtra?: ReactNode;
}

export function OrgFlow(props: OrgFlowProps) {
  // ต้องมี Provider ครอบ ถึงจะเรียก useReactFlow() เพื่อสั่ง fitView เองได้
  return (
    <ReactFlowProvider>
      <OrgFlowInner {...props} />
    </ReactFlowProvider>
  );
}

function OrgFlowInner({
  tree,
  mode = 'staffing',
  metric = 'total',
  fiscalYear,
  dir = 'TB',
  onOpenList,
  onScope,
  selected = null,
  bare = false,
  onExecutives,
  people,
  peopleMaxTier,
  onOpenPosition,
  toolsExtra,
}: OrgFlowProps) {
  /**
   * กิ่งที่กางอยู่ตอนเปิดมา
   *
   * ดูทั้งกระทรวง  → กางกระทรวงกับกลุ่มภารกิจทั้งสี่ จึงเห็นส่วนราชการครบทุกแห่งทันที
   * เจาะดูกรมเดียว → กางแค่ตัวกรมนั้น จึงเห็นสำนักในกรมครบ
   *
   * ⚠ ตอนดูทั้งกระทรวงต้องกางสองชั้น ไม่ใช่ชั้นเดียว
   * ก่อนมีชั้นกลุ่มภารกิจ กางชั้นเดียวก็เห็นกรมครบแล้ว พอแทรกกลุ่มภารกิจเข้ามา
   * กางชั้นเดียวจะเหลือกล่องกลุ่มภารกิจสี่ใบบนจอเปล่า ๆ ซึ่งตอบคำถามแรกของผู้บริหาร
   * ("ในกระทรวงมีส่วนราชการอะไรบ้าง") ไม่ได้เลย
   *
   * ⚠ แต่ห้ามกางลึกกว่านี้ เดิมเคยกางส่วนราชการทุกแห่งไว้ให้ด้วย ซึ่งลากตำแหน่ง
   * รายอัตราของแต่ละแห่งขึ้นมาทั้งหมด ผังกว้างเกินจอ พอ fitView ย่อให้พอดี
   * การ์ดเล็กจนอ่านชื่อหน่วยไม่ออก — ผังที่อ่านไม่ออกทั้งผืนแย่กว่าผังที่ต้องกดกางอีกครั้ง
   */
  const [open, setOpen] = useState<Set<string>>(() => defaultOpen(tree));

  /** กิ่งที่เพิ่งกดกาง — ใช้เล็งกล้องไปที่กิ่งนั้น ไม่ใช่จัดทั้งผังใหม่ */
  const [focus, setFocus] = useState<string | null>(null);

  const toggle = useCallback((id: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setFocus(null);
      } else {
        next.add(id);
        setFocus(id);
      }
      return next;
    });
  }, []);

  const openList = useCallback(
    (id: string, mode: PositionListMode) => onOpenList?.(id, mode),
    [onOpenList],
  );

  const expandAll = useCallback(() => {
    setFocus(null);
    const ids = new Set<string>();
    const walk = (n: OrgNode) => {
      ids.add(n.id);
      for (const c of n.children ?? []) walk(c);
    };
    for (const r of tree) walk(r);
    setOpen(ids);
  }, [tree]);

  const collapseAll = useCallback(() => {
    setFocus(null);
    setOpen(defaultOpen(tree));
  }, [tree]);


  /* กิ่งที่ยังต้องวาด — ตัดเหลือเฉพาะสายที่เลือกระหว่างที่แผงข้างเปิดอยู่ (ดู lineageIds) */
  const lineage = useMemo(() => lineageIds(tree, selected ?? null), [tree, selected]);

  /**
   * กางกิ่งเหนือหน่วยที่เลือกให้เอง
   *
   * ⚠ ถ้าไม่ทำ การกดตัวเลขของหน่วยที่อยู่ลึก (เช่นสำนักในกรมในกลุ่มภารกิจ)
   *   จะได้ผังว่างเปล่า เพราะตัวกรอง lineage เหลือไว้แค่สายนั้น แต่กิ่งเหนือมันยังยุบอยู่
   *   จึงไม่มีลูกให้วาดเลยสักใบ — กรองกับกางต้องทำคู่กันเสมอ
   *
   * ⚠ เติมเข้าไปในชุดเดิม ไม่ใช่แทนที่ — กิ่งที่ผู้ใช้กางไว้เองต้องไม่ถูกยุบทิ้ง
   *   เพราะเขาไม่ได้สั่งให้ยุบ และพอปิดแผง ผังจะกลับมาเหมือนก่อนกดพอดี
   */
  useEffect(() => {
    const path = ancestorPath(tree, selected ?? null);
    if (path.length === 0) return;
    setOpen((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of path) if (!next.has(id)) { next.add(id); changed = true; }
      return changed ? next : prev;
    });
  }, [tree, selected]);

  /* ⚠ ผังคนไม่มีสถานะกาง/ยุบ — มีแค่ 32 โหนดและจัดเป็นแถบตามลำดับ
     การยุบแถบไม่ได้ช่วยอะไร และการมี state ที่ไม่ได้ใช้จะทำให้คนอ่านโค้ด
     เข้าใจว่าผังนี้กางได้แล้วไปแก้ผิดที่ */
  const openPosition = useCallback((posId: string) => onOpenPosition?.(posId), [onOpenPosition]);

  /* ⚠ ประกาศชนิดเป็น Node[] กว้าง ๆ โดยตั้งใจ
     สองผังใช้โหนดคนละชนิด (UnitNode กับ PersonFlowNode) ซึ่งรวมกันไม่ได้
     ถ้าปล่อยให้ TypeScript อนุมานเอง มันจะได้ union แล้ว <ReactFlow> รับไม่ได้
     ความปลอดภัยของชนิดอยู่ที่ตัวสร้างโหนดทั้งสองตัวแล้ว ไม่ได้หายไปไหน */
  const { nodes, edges } = useMemo<{ nodes: Node[]; edges: Edge[] }>(() => {
    /* ผังคนแทนที่ผังหน่วยงานทั้งผืน ไม่ใช่วาดซ้อนกัน */
    if (people?.root) return buildPeopleGraph(people, dir, openPosition, peopleMaxTier);
    return buildGraph(tree, mode, dir, open, selected, toggle, openList, onScope, onExecutives, metric, fiscalYear, lineage);
  }, [people, peopleMaxTier, dir, openPosition,
      tree, mode, open, selected, toggle, openList, onScope, onExecutives, metric, fiscalYear, lineage]);

  const { fitView, getViewport, setViewport } = useReactFlow();

  /*
   * บันทึกรูปผัง
   *
   * ⚠ ต้องมีสถานะ "กำลังบันทึก" ให้เห็น เหมือนปุ่ม PDF/Excel
   *   ครั้งแรกที่กดต้องโหลดไลบรารีก่อน แล้วยังต้องวาดผังทั้งกรอบลงรูปอีก
   *   บนเครื่องช้าหรือผังที่กางหลายกิ่ง กินเวลาหลายวินาทีโดยหน้าจอไม่ขยับเลย
   *   ถ้าปุ่มไม่เปลี่ยนอะไร ผู้ใช้จะกดซ้ำแล้วได้ไฟล์ซ้ำหลายใบ
   *
   * ⚠ ล้มเหลวแล้วต้องบอก ไม่ใช่เงียบ — ปุ่มที่กดแล้วไม่มีอะไรเกิดขึ้น
   *   ผู้ใช้จะสรุปว่าระบบพัง ไม่ใช่ว่าการบันทึกรอบนี้ไม่ผ่าน
   */
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const savePng = useCallback(async () => {
    const el = box.current;
    if (!el || saving) return;
    setSaving(true);
    setSaveErr(null);
    /*
     * จำมุมมองปัจจุบันไว้ก่อน แล้วคืนให้ตอนจบ
     *
     * ⚠ ต้องคืนใน finally ไม่ใช่ท้าย try — ถ้าการสร้างรูปพัง แล้วเราไม่คืน
     *   ผู้ใช้จะเหลือผังที่ถูกย่อจนไม่รู้ว่าตัวเองอยู่ตรงไหน
     *   ซึ่งแย่กว่าการที่รูปเซฟไม่สำเร็จเสียอีก
     */
    const before = getViewport();
    try {
      /*
       * จัดผังให้พอดีกรอบก่อนถ่าย
       *
       * นี่คือหัวใจของการแก้ปัญหา "รูปถูกตัด" — ถ่ายกรอบแคนวาสเฉย ๆ จะได้เฉพาะ
       * ส่วนที่ผู้ใช้เลื่อนมาเห็นตอนนั้น ผังใหญ่กว่าจอเมื่อไรก็ขาดเมื่อนั้น
       * พอ fitView ก่อน ทุกกิ่งที่กางอยู่จะเข้ามาอยู่ในกรอบครบตั้งแต่ก่อนถ่าย
       * เราจึงไม่ต้องคำนวณขนาดหรือ transform เองเลย
       *
       * ⚠ duration: 0 — ต้องขยับทันที ห้ามใส่ animation
       *   ถ้าใส่ ภาพจะถูกถ่ายระหว่างที่ผังยังเลื่อนอยู่ แล้วได้รูปที่เบี้ยว
       */
      await fitView({ padding: 0.08, duration: 0 });

      /*
       * รอให้เบราว์เซอร์วาดตำแหน่งใหม่จริง ๆ ก่อน
       * fitView คืนค่ากลับมาตั้งแต่ตอนที่สั่ง transform ใหม่แล้ว แต่ยังไม่ได้วาด
       * ถ้าถ่ายทันทีจะได้ภาพของเฟรมก่อนหน้า ซึ่งคือมุมมองเดิมที่ยังไม่ได้จัด
       */
      await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

      /*
       * ส่งระดับซูมหลังจัดพอดีจอไปด้วย เพื่อให้ตัวหนังสือในรูปคม
       *
       * ⚠ ต้องอ่านหลัง fitView ไม่ใช่ก่อน — ค่าที่ต้องชดเชยคือระดับที่ผังถูกย่อ
       *   ตอนถ่าย ไม่ใช่ระดับที่ผู้ใช้ตั้งไว้ก่อนกดปุ่ม
       *   ถ้าอ่านผิดจังหวะ ผังใหญ่ ๆ จะได้รูปเบลอเหมือนเดิม
       */
      await exportPng(el, 'ผังองค์กร', { zoom: getViewport().zoom });
    } catch (err) {
      setSaveErr(err instanceof Error ? err.message : 'บันทึกรูปไม่สำเร็จ');
    } finally {
      setViewport(before, { duration: 0 });
      setSaving(false);
    }
  }, [saving, fitView, getViewport, setViewport]);

  /** จัดผังให้พอดีจอ — ปุ่มเดียวกับที่เรียกอัตโนมัติตอนสลับทิศ */
  const fitAll = useCallback(() => {
    setFocus(null);
    void fitView({ padding: 0.14, maxZoom: 1, duration: 350 });
  }, [fitView]);

  /**
   * กาง/ยุบ/สลับทิศแล้วต้องเล็งกล้องใหม่
   *
   * ถ้ากดกางกิ่งใดกิ่งหนึ่ง ให้เล็งไปที่กิ่งนั้นกับสิ่งที่เพิ่งโผล่ออกมา ไม่ใช่จัดทั้งผังให้พอดีจอ
   * เพราะการ fit ทั้งผัง (ซึ่งอาจกว้าง 7 ชั้น) จะย่อจนตัวหนังสือเล็กเกินอ่าน
   * ทั้งที่ผู้ใช้แค่อยากดูหน่วยเดียวที่เพิ่งกด
   *
   * ⚠ ต้องหน่วงหนึ่งเฟรมเพื่อรอ React Flow วัดขนาดโหนดใหม่ให้เสร็จก่อน
   * ถ้าสั่ง fitView ทันที มันจะคำนวณจากขนาดเดิมแล้วผังจะโดนตัดขอบล่าง
   * ซึ่งเป็นอาการเดียวกับที่เจอในผังของเว็บต้นแบบ
   */
  /**
   * เล็งกล้องของผังคน — คนละกฎกับผังหน่วยงาน
   *
   * ────────────────────────────────────────────────────────────────
   *  ทำไมใช้ fitView ธรรมดาไม่ได้
   * ────────────────────────────────────────────────────────────────
   *
   * fitView ย่อให้ "ทั้งผัง" อยู่ในกรอบ จึงเลือกอัตราส่วนที่เล็กกว่าระหว่างกว้างกับสูง
   * ผังคนสูง 3,368 พิกเซล ส่วนกรอบแคนวาสสูงราว 530 อัตราส่วนจึงเป็น 0.16
   * ผลคือการ์ดเล็กจนอ่านชื่อไม่ออก และเส้นหนา 1.4 พิกเซลบางเหลือ 0.2 จนหายไปจากจอ
   * ซึ่งคือสิ่งที่ผู้ใช้เห็นแล้วบอกว่า "มันแปลก ๆ"
   *
   * ผังคนกว้างไม่เกิน 12 คอลัมน์เสมอ (ดู colsFor ใน OrgPeople.tsx) แต่สูงได้เรื่อย ๆ
   * ตามจำนวนคนในลำดับที่ใหญ่ที่สุด จึงเล็งด้วยความกว้างแล้วชิดขอบบนแทน
   * ผู้ใช้เห็นโครงทั้งแนวนอนตั้งแต่แรกในขนาดที่อ่านได้ แล้วเลื่อนลงไปดูลำดับล่าง
   *
   * ⚠ ห้ามให้ zoom ต่ำกว่า MIN_PEOPLE_ZOOM แม้จอจะแคบแค่ไหน
   *   ผังที่ย่อจนอ่านไม่ออกไม่ได้ให้ข้อมูลอะไรเลย การให้เลื่อนดูยังดีกว่า
   */
  const fitPeople = useCallback(() => {
    const el = box.current;
    if (!el || nodes.length === 0) return;
    const rect = el.getBoundingClientRect();
    if (rect.width < 80 || rect.height < 80) return;

    const minX = Math.min(...nodes.map((n) => n.position.x));
    const maxX = Math.max(...nodes.map((n) => n.position.x)) + PERSON_CARD.w;
    const minY = Math.min(...nodes.map((n) => n.position.y));
    const w = maxX - minX;

    const PAD = 28;
    const MIN_PEOPLE_ZOOM = 0.4;
    const zoom = Math.min(1, Math.max(MIN_PEOPLE_ZOOM, (rect.width - PAD * 2) / w));

    setViewport(
      { x: (rect.width - w * zoom) / 2 - minX * zoom, y: PAD - minY * zoom, zoom },
      { duration: 300 },
    );
  }, [nodes, setViewport]);

  useEffect(() => {
    const t = setTimeout(() => {
      /* ⚠ ผังคนต้องออกก่อน ไม่งั้น fitView ข้างล่างจะย่อจนอ่านไม่ออก */
      if (people?.root) {
        fitPeople();
        return;
      }
      if (focus) {
        const kin = nodes
          .filter((n) => n.id === focus || n.id.startsWith(`pos:${focus}:`))
          .map((n) => ({ id: n.id }));
        const childUnits = edges.filter((e) => e.source === focus).map((e) => ({ id: e.target }));
        void fitView({ nodes: [...kin, ...childUnits], padding: 0.18, maxZoom: 1, duration: 400 });
      } else {
        void fitView({ padding: 0.14, maxZoom: 1, duration: 350 });
      }
    }, 60);
    return () => clearTimeout(t);
  }, [nodes, edges, focus, fitView, people, fitPeople]);

  /**
   * แคนวาสถูกย่อ/ขยายเมื่อแผงข้างเปิด-ปิด ต้องจัดผังใหม่ให้พอดีพื้นที่ที่เหลือ
   *
   * เว็บต้นแบบไม่ได้ทำข้อนี้ พอเปิดแผงข้างแล้วผังยังอยู่ที่เดิม ครึ่งซ้ายที่เหลือจึงว่างเปล่า
   * ส่วนกิ่งที่กำลังดูอยู่ไปหลบอยู่หลังแผง
   */
  const box = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let timer: ReturnType<typeof setTimeout>;
    const ro = new ResizeObserver(() => {
      clearTimeout(timer);
      /* ⚠ ต้องแยกกฎเหมือนตัวข้างบน ไม่งั้นแค่ย่อ-ขยายหน้าต่างครั้งเดียว
         ผังคนก็จะถูก fitView ย่อกลับไปเล็กจนอ่านไม่ออกอีก */
      timer = setTimeout(() => {
        if (people?.root) fitPeople();
        else void fitView({ padding: 0.14, maxZoom: 1, duration: 250 });
      }, 180);
    });
    ro.observe(el);
    return () => {
      clearTimeout(timer);
      ro.disconnect();
    };
  }, [fitView, people, fitPeople]);

  /**
   * เปลี่ยนหน่วยที่เลือกทั้งที่แผงยังเปิดอยู่ ก็ต้องจัดผังใหม่
   *
   * ⚠ ResizeObserver ข้างบนไม่ครอบกรณีนี้ — แคนวาสกว้างเท่าเดิมทุกอย่าง
   *   สิ่งที่เปลี่ยนคือจำนวนกิ่งที่เหลืออยู่ในผัง (ดู lineageOf)
   *   ถ้าไม่จัดใหม่ สายที่เพิ่งเลือกจะไปโผล่นอกจอในระดับซูมของสายก่อนหน้า
   */
  useEffect(() => {
    const t = setTimeout(() => void fitView({ padding: 0.14, maxZoom: 1, duration: 250 }), 60);
    return () => clearTimeout(t);
  }, [selected, fitView]);

  return (
    <div className="flowbox">
      {!bare && (
        /*
         * ปุ่มไอคอนล้วน — ต้องมีทั้ง aria-label และ data-tip เสมอ
         *   aria-label  ชื่อปุ่มสำหรับโปรแกรมอ่านหน้าจอ ซึ่งไม่เห็นไอคอน
         *   data-tip    ข้อความที่ CSS เอาไปวาดเป็นคำอธิบายตอนเอาเมาส์ชี้
         * ใช้ของเราเองแทน title ของเบราว์เซอร์ เพราะ title รอเกือบวินาทีกว่าจะขึ้น
         * และจัดรูปแบบไม่ได้เลย ผู้ใช้ที่ไม่รู้ว่าไอคอนแปลว่าอะไรมักเลิกรอไปก่อน
         */
        <div className="flow-tools">
          <ToolFan>
            <button type="button" className="icobtn" onClick={expandAll} aria-label="กางทั้งหมด" data-tip="กางทั้งหมด">
              <PlusSquareOutlined />
            </button>
            <button type="button" className="icobtn" onClick={collapseAll} aria-label="ยุบทั้งหมด" data-tip="ยุบทั้งหมด">
              <MinusSquareOutlined />
            </button>
            {/* เดิมมีปุ่ม "จัดให้พอดีจอ" ตรงนี้ เอาออกเพราะซ้ำกับปุ่มวงกลมในชุดซูมมุมล่างขวา
                ซึ่งอยู่ติดกับ + และ − ที่ผู้ใช้กดอยู่แล้วตอนปรับมุมมอง

                ⚠ fitAll ยังอยู่และยังจำเป็น — ชุดซูมเรียกใช้ผ่าน prop onFit ของ FlowZoom
                  และปุ่มบันทึกรูปก็พึ่ง fitView ตัวเดียวกันก่อนถ่าย */}
            <button
              type="button"
              className="icobtn"
              onClick={savePng}
              disabled={saving}
              /* ⚠ ข้อความต้องไม่บอกว่า "ตามที่เห็น" อีกแล้ว
                 ปุ่มนี้จัดผังให้พอดีกรอบก่อนถ่ายเสมอ รูปที่ได้จึงครบทุกกิ่งที่กางอยู่
                 ไม่ใช่เฉพาะมุมที่ผู้ใช้เลื่อนมาดู */
              aria-label={saving ? 'กำลังบันทึกรูปผัง' : 'บันทึกรูปผังทั้งผังเป็นไฟล์ PNG'}
              data-tip={saveErr ?? (saving ? 'กำลังบันทึก…' : 'บันทึกรูปผัง (PNG) — ได้ครบทุกกิ่งที่กางอยู่')}
            >
              {saving ? <LoadingOutlined /> : <CameraOutlined />}
            </button>
            {toolsExtra}
          </ToolFan>
        </div>
      )}

      {/* ⚠ แถบข้อความใต้ผังต้องอยู่ "นอก" กรอบแคนวาส ไม่ใช่ลอยทับ
          React Flow วัดพื้นที่จากกรอบที่มันอยู่ ถ้าแถบลอยทับ มันจะนับพื้นที่ใต้แถบเป็นที่ว่างด้วย
          แล้ว fitView จะวางกิ่งล่างสุดไว้หลังแถบพอดี ซึ่งเป็นกิ่งที่มองไม่เห็นตลอดกาล */}
      <div className="flow-canvas" ref={box}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.14, maxZoom: 1 }}
        minZoom={0.08}
        maxZoom={1.6}
        nodesDraggable={false}
        proOptions={{ hideAttribution: false }}
      >
        {/* ไม่ใส่ <MiniMap> โดยตั้งใจ — ผังนี้กางทีละกิ่ง จำนวนโหนดบนจอจึงไม่เคยมากพอ
            ที่จะหลงทางจนต้องมีแผนที่ย่อ มันกินมุมขวาล่างไปโดยไม่มีใครใช้
            ปุ่ม "จัดให้พอดีจอ" ตอบโจทย์เดียวกันด้วยการกดครั้งเดียว

            ไม่ใส่ <Background> ด้วย — เดิมเป็นลายจุดถี่ทั้งพื้น
            พอมีลายอัตลักษณ์อยู่กลางแคนวาสแล้ว (ดู .flow-canvas::before ใน styles.css)
            สองลายซ้อนกันกลายเป็นพื้นที่ยุ่ง แล้วลายอัตลักษณ์อ่านไม่ออกว่าเป็นลายอะไร
            เอาจุดออกให้เหลือลายเดียว */}
      </ReactFlow>

      {/* ปุ่มควบคุมของเราเอง แทน <Controls> ของ React Flow
          เพราะชุดเดิมเป็นไอคอนล้วนที่ไม่มีชื่อให้โปรแกรมอ่านหน้าจอ และปุ่มเล็กกว่า 44px */}
      <FlowZoom onFit={fitAll} />
      </div>

    </div>
  );
}

/** ปุ่มซูมมุมขวาล่าง — ทุกปุ่มมีชื่อกำกับและสูง 40px ตามเกณฑ์ขนาดเป้าสัมผัส */
function FlowZoom({ onFit }: { onFit: () => void }) {
  const { zoomIn, zoomOut } = useReactFlow();
  return (
    <div className="flow-zoom" data-noexport="true">
      <button type="button" onClick={() => zoomIn({ duration: 200 })} aria-label="ซูมเข้า">+</button>
      <button type="button" onClick={onFit} aria-label="จัดผังให้พอดีจอ">⌾</button>
      <button type="button" onClick={() => zoomOut({ duration: 200 })} aria-label="ซูมออก">−</button>
    </div>
  );
}

/* ---------------- ตัวช่วยจัดรูปแบบ ---------------- */

const TH_MONTH_SHORT = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
];

function thaiShort(iso: string): string {
  const [y, m] = iso.split('-');
  if (!y || !m) return iso;
  return `${TH_MONTH_SHORT[Number(m) - 1]} ${Number(y) + 543}`;
}

export { CURRENT_FY };
